import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { analyticsAPI, monitoringAPI, serverAPI, assetsAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import useDocumentTitle from '../../hooks/useDocumentTitle';

// ─── Types ────────────────────────────────────────────────────────────────────
interface HubAsset { id: number; name: string; tracking_id: string }

type MonStatus = 'up' | 'down' | 'degraded' | 'no_urls' | 'loading';

interface AssetCard {
  asset: HubAsset;
  liveUsers: number;
  monStatus: MonStatus;
  serversTotal: number;
  serversDown: number;
  securityWarnings: number;
  loading: boolean;
}

interface LiveVisitor {
  visitorId: string;
  country: string;
  city?: string;
  device: string;
  browser?: string;
  currentPage?: string;
  sessionDuration: number;
  pageViews: number;
  lastActivity: string;
  joinedAt: string;
}

interface MonCheck {
  url: string;
  status: 'up' | 'down';
  response_time?: number;
  status_code?: number;
  error_message?: string;
  checked_at: string;
}

interface ServerInfo {
  id: number;
  name: string;
  status: string;
  last_metrics?: {
    cpu_usage?: number;
    memory_usage?: number;
    disk_usage?: number;
  };
}

interface ModalData {
  liveVisitors: LiveVisitor[];
  monChecks: MonCheck[];
  servers: ServerInfo[];
  loading: boolean;
}

const CARDS_OPTIONS = [4, 6, 8, 12];
const REFRESH_OPTIONS = [
  { label: '15s', value: 15 },
  { label: '30s', value: 30 },
  { label: '1m', value: 60 },
  { label: '2m', value: 120 },
];

// Overall card health: 'ok' | 'warn' | 'critical'
const cardHealth = (c: AssetCard): 'ok' | 'warn' | 'critical' => {
  if (c.monStatus === 'down') return 'critical';
  if (c.serversDown > 0) return 'critical';
  if (c.monStatus === 'degraded') return 'warn';
  if (c.securityWarnings > 0) return 'warn';
  return 'ok';
};

const countryFlag = (code: string) => {
  if (!code || code.length !== 2) return '🌐';
  const u = code.toUpperCase();
  return String.fromCodePoint(0x1F1E6 + u.charCodeAt(0) - 65) +
    String.fromCodePoint(0x1F1E6 + u.charCodeAt(1) - 65);
};

const timeAgo = (iso: string) => {
  const secs = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  return `${Math.floor(secs / 3600)}h ago`;
};

const fmtTime = (iso: string) => new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });

const metricBar = (val: number | undefined, color: string) => {
  const v = Math.min(100, Math.round(val ?? 0));
  const danger = v >= 90;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1 }}>
      <div style={{ flex: 1, height: 5, background: 'rgba(255,255,255,0.06)', borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${v}%`, background: danger ? '#ef4444' : color, borderRadius: 3, transition: 'width .3s' }} />
      </div>
      <span style={{ fontSize: '10px', color: danger ? '#f87171' : '#94a3b8', fontWeight: 600, minWidth: 28 }}>{v}%</span>
    </div>
  );
};

// ─── Animated Wave Icon (for live users) ─────────────────────────────────────
const WaveIcon: React.FC<{ color: string }> = ({ color }) => (
  <span style={{ display: 'inline-flex', alignItems: 'flex-end', gap: 2, height: 14, verticalAlign: 'middle', marginRight: 4 }}>
    {[0, 100, 200, 100, 0].map((delay, i) => (
      <span key={i} style={{
        display: 'inline-block', width: 3, borderRadius: 2, background: color,
        animation: `hubWave 1s ease-in-out infinite`,
        animationDelay: `${delay}ms`,
        height: i === 2 ? 14 : i === 1 || i === 3 ? 10 : 6,
      }} />
    ))}
  </span>
);

// ─── Heartbeat Icon (for servers) ────────────────────────────────────────────
const HeartbeatIcon: React.FC<{ color: string }> = ({ color }) => (
  <span style={{ display: 'inline-block', animation: 'hubBeat 1.4s ease-in-out infinite', verticalAlign: 'middle', marginRight: 4, color }}>
    <i className="bi bi-heart-pulse-fill" style={{ fontSize: 13 }}></i>
  </span>
);

// ─── Card Detail Modal ────────────────────────────────────────────────────────
const CardDetailModal: React.FC<{ card: AssetCard; onClose: () => void }> = ({ card, onClose }) => {
  const navigate = useNavigate();
  const { asset, monStatus, serversTotal, serversDown } = card;
  const health = cardHealth(card);
  const [modal, setModal] = useState<ModalData>({ liveVisitors: [], monChecks: [], servers: [], loading: true });
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchModalData = useCallback(async () => {
    const result: Partial<ModalData> = {};
    await Promise.allSettled([
      analyticsAPI.getLiveVisitors(asset.id).then(r => {
        result.liveVisitors = r.data?.data?.visitors ?? [];
      }),
      monitoringAPI.getHealthLog(asset.id, 2).then(r => {
        result.monChecks = r.data?.data?.recent_checks ?? [];
      }),
      serverAPI.list({ asset_id: asset.id }).then(r => {
        result.servers = r.data?.data?.servers ?? r.data?.data ?? [];
      }),
    ]);
    setModal(prev => ({ ...prev, ...result, loading: false }));
  }, [asset.id]);

  useEffect(() => {
    fetchModalData();
    timerRef.current = setInterval(fetchModalData, 15000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [fetchModalData]);

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const statusColor = health === 'critical' ? '#ef4444' : health === 'warn' ? '#f59e0b' : '#10b981';
  const statusLabel = monStatus === 'down' ? 'DOWN' : monStatus === 'degraded' ? 'DEGRADED' : monStatus === 'up' ? 'ONLINE' : monStatus === 'no_urls' ? 'NO URL' : '...';

  // Group monitoring checks by URL for a cleaner log
  const downChecks = modal.monChecks.filter(c => c.status === 'down');

  return (
    <div
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}
    >
      <div style={{
        background: '#0f0f27', border: '1px solid rgba(99,102,241,0.3)', borderRadius: 18,
        width: '100%', maxWidth: 1020, maxHeight: '88vh', display: 'flex', flexDirection: 'column',
        boxShadow: '0 32px 80px rgba(0,0,0,0.7)',
      }}>
        {/* Header */}
        <div style={{ padding: '16px 22px', borderBottom: '1px solid rgba(99,102,241,0.15)', display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <h5 style={{ margin: 0, color: '#e2e8f0', fontWeight: 700, fontSize: '1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {asset.name}
              </h5>
              <span style={{
                background: `${statusColor}22`, border: `1px solid ${statusColor}55`,
                borderRadius: 20, padding: '2px 10px', fontSize: '0.7rem', fontWeight: 700, color: statusColor,
                display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0,
              }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: statusColor, display: 'inline-block', animation: health === 'critical' ? 'hubRedPulse 1.2s infinite' : 'hubGreenPulse 2.5s infinite' }} />
                {statusLabel}
              </span>
              {serversTotal > 0 && (
                <span style={{ fontSize: '0.7rem', color: serversDown > 0 ? '#f87171' : '#a5b4fc', background: 'rgba(99,102,241,0.1)', borderRadius: 12, padding: '2px 8px', flexShrink: 0 }}>
                  <i className="bi bi-server me-1"></i>{serversTotal - serversDown}/{serversTotal} servers
                </span>
              )}
            </div>
            <div style={{ fontSize: '0.65rem', color: '#475569', marginTop: 3, fontFamily: 'monospace' }}>{asset.tracking_id}</div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
            <button
              onClick={() => { onClose(); navigate('/analytics/summary'); }}
              style={{ background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', border: 'none', borderRadius: 8, padding: '7px 16px', color: '#fff', fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
            >
              <i className="bi bi-arrow-up-right-square"></i>Go to Asset
            </button>
            <button onClick={onClose} style={smallBtn}>
              <i className="bi bi-x-lg"></i>
            </button>
          </div>
        </div>

        {/* Body */}
        <div style={{ flex: 1, display: 'flex', minHeight: 0, overflow: 'hidden' }}>
          {/* ── Left: Live Visitors ── */}
          <div style={{ width: '44%', borderRight: '1px solid rgba(99,102,241,0.12)', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '12px 18px 8px', borderBottom: '1px solid rgba(99,102,241,0.08)', flexShrink: 0 }}>
              <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#6366f1', textTransform: 'uppercase', letterSpacing: '0.07em', display: 'flex', alignItems: 'center', gap: 6 }}>
                {monStatus === 'down' ? (
                  <><span style={{ width: 8, height: 8, borderRadius: '50%', background: '#ef4444', display: 'inline-block', animation: 'hubRedPulse 1.2s infinite' }} />
                  Live Stopped — Site Down</>
                ) : (
                  <><WaveIcon color="#6366f1" />Live Visitors ({card.liveUsers})</>
                )}
              </div>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
              {modal.loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '24px' }}>
                  <div className="spinner-border spinner-border-sm" style={{ color: '#6366f1' }}></div>
                </div>
              ) : modal.liveVisitors.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#334155', padding: '32px 16px' }}>
                  {monStatus === 'down'
                    ? <><i className="bi bi-wifi-off" style={{ fontSize: '2rem', color: '#ef4444', display: 'block', marginBottom: 8 }}></i><span style={{ color: '#64748b', fontSize: '0.82rem' }}>No active visitors — site is unreachable</span></>
                    : <><i className="bi bi-person-x" style={{ fontSize: '2rem', color: '#334155', display: 'block', marginBottom: 8 }}></i><span style={{ color: '#64748b', fontSize: '0.82rem' }}>No active visitors right now</span></>
                  }
                </div>
              ) : (
                modal.liveVisitors.map((v, i) => (
                  <div key={i} style={{
                    display: 'flex', alignItems: 'flex-start', gap: 10, padding: '9px 18px',
                    borderBottom: '1px solid rgba(255,255,255,0.03)',
                  }}>
                    {/* Flag + country */}
                    <div style={{ flexShrink: 0, fontSize: '18px', lineHeight: 1, marginTop: 1 }}>
                      {countryFlag(v.country)}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ color: '#cbd5e1', fontSize: '0.8rem', fontWeight: 600, display: 'flex', gap: 6, alignItems: 'center' }}>
                        <span>{v.country}{v.city ? `, ${v.city}` : ''}</span>
                        <span style={{ fontSize: '10px', color: '#475569', background: 'rgba(255,255,255,0.05)', borderRadius: 4, padding: '1px 5px' }}>
                          <i className={`bi ${v.device === 'Mobile' ? 'bi-phone' : v.device === 'Tablet' ? 'bi-tablet' : 'bi-display'} me-1`}></i>
                          {v.device}
                        </span>
                      </div>
                      {v.currentPage && (
                        <div style={{ fontSize: '10px', color: '#475569', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={v.currentPage}>
                          <i className="bi bi-file-earmark me-1"></i>{v.currentPage}
                        </div>
                      )}
                    </div>
                    <div style={{ flexShrink: 0, textAlign: 'right' }}>
                      <div style={{ fontSize: '10px', color: '#10b981', fontWeight: 600 }}>
                        <i className="bi bi-arrow-right-circle me-1"></i>{fmtTime(v.joinedAt)}
                      </div>
                      <div style={{ fontSize: '10px', color: '#475569', marginTop: 2 }}>
                        <i className="bi bi-clock me-1"></i>{timeAgo(v.lastActivity)}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* ── Right: Mon Log + Servers ── */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

            {/* Monitoring Log */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', borderBottom: '1px solid rgba(99,102,241,0.12)', minHeight: 0 }}>
              <div style={{ padding: '12px 18px 8px', borderBottom: '1px solid rgba(99,102,241,0.08)', flexShrink: 0 }}>
                <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#6366f1', textTransform: 'uppercase', letterSpacing: '0.07em', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span><i className="bi bi-activity me-1"></i>Monitoring Log (last 2h)</span>
                  {downChecks.length > 0 && (
                    <span style={{ background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 10, padding: '2px 8px', color: '#f87171', fontSize: '10px' }}>
                      {downChecks.length} outage{downChecks.length !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>
              </div>
              <div style={{ flex: 1, overflowY: 'auto' }}>
                {modal.loading ? (
                  <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}>
                    <div className="spinner-border spinner-border-sm" style={{ color: '#6366f1' }}></div>
                  </div>
                ) : modal.monChecks.length === 0 ? (
                  <div style={{ textAlign: 'center', color: '#334155', padding: '24px 16px', fontSize: '0.8rem' }}>
                    <i className="bi bi-globe me-1"></i>No monitoring URLs configured
                  </div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
                    <tbody>
                      {modal.monChecks.slice(0, 30).map((chk, i) => {
                        const isDown = chk.status === 'down';
                        return (
                          <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.03)', background: isDown ? 'rgba(239,68,68,0.05)' : 'transparent' }}>
                            <td style={{ padding: '6px 12px 6px 18px', width: 22 }}>
                              <i className={`bi ${isDown ? 'bi-x-circle-fill' : 'bi-check-circle-fill'}`} style={{ color: isDown ? '#ef4444' : '#10b981', fontSize: 13 }}></i>
                            </td>
                            <td style={{ padding: '6px 4px', color: '#94a3b8' }}>
                              {fmtTime(chk.checked_at)}
                            </td>
                            <td style={{ padding: '6px 4px', color: isDown ? '#f87171' : '#34d399', fontWeight: isDown ? 700 : 400 }}>
                              {isDown ? (
                                <span title={chk.error_message || ''}>
                                  DOWN {chk.error_message ? <i className="bi bi-info-circle ms-1" style={{ fontSize: 10, cursor: 'help' }} title={chk.error_message}></i> : ''}
                                </span>
                              ) : `${chk.response_time ?? '-'}ms`}
                            </td>
                            <td style={{ padding: '6px 18px 6px 4px', color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 180 }} title={chk.url}>
                              {chk.url?.replace(/^https?:\/\//, '')}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            {/* Servers */}
            <div style={{ flexShrink: 0, maxHeight: '38%', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              <div style={{ padding: '10px 18px 7px', borderBottom: '1px solid rgba(99,102,241,0.08)', flexShrink: 0 }}>
                <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#6366f1', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
                  <i className="bi bi-server me-1"></i>Servers
                  {serversDown > 0 && (
                    <span style={{ marginLeft: 8, background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 10, padding: '2px 7px', color: '#f87171', fontSize: '10px' }}>
                      {serversDown} offline
                    </span>
                  )}
                </div>
              </div>
              <div style={{ overflowY: 'auto', flex: 1 }}>
                {modal.loading ? (
                  <div style={{ display: 'flex', justifyContent: 'center', padding: 16 }}>
                    <div className="spinner-border spinner-border-sm" style={{ color: '#6366f1' }}></div>
                  </div>
                ) : modal.servers.length === 0 ? (
                  <div style={{ textAlign: 'center', color: '#334155', padding: '16px', fontSize: '0.78rem' }}>
                    No servers linked to this asset
                  </div>
                ) : (
                  modal.servers.map((srv, i) => {
                    const offline = srv.status === 'offline' || srv.status === 'down';
                    const m = srv.last_metrics;
                    return (
                      <div key={i} style={{ padding: '8px 18px', borderBottom: '1px solid rgba(255,255,255,0.03)', background: offline ? 'rgba(239,68,68,0.04)' : 'transparent' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: offline ? 0 : 5 }}>
                          <span style={{ width: 7, height: 7, borderRadius: '50%', background: offline ? '#ef4444' : '#10b981', display: 'inline-block', animation: offline ? 'hubRedPulse 1.2s infinite' : 'hubGreenPulse 3s infinite' }} />
                          <span style={{ color: offline ? '#f87171' : '#cbd5e1', fontWeight: 600, fontSize: '0.8rem', flex: 1 }}>{srv.name}</span>
                          <span style={{ fontSize: '10px', color: offline ? '#ef4444' : '#10b981', fontWeight: 700 }}>{offline ? 'OFFLINE' : 'ONLINE'}</span>
                        </div>
                        {!offline && m && (
                          <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', rowGap: 3, columnGap: 8, marginTop: 4 }}>
                            <span style={{ fontSize: '10px', color: '#475569', fontWeight: 600, lineHeight: '14px' }}>CPU</span>
                            {metricBar(m.cpu_usage, '#6366f1')}
                            <span style={{ fontSize: '10px', color: '#475569', fontWeight: 600, lineHeight: '14px' }}>MEM</span>
                            {metricBar(m.memory_usage, '#8b5cf6')}
                            <span style={{ fontSize: '10px', color: '#475569', fontWeight: 600, lineHeight: '14px' }}>DSK</span>
                            {metricBar(m.disk_usage, '#06b6d4')}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: '10px 22px', borderTop: '1px solid rgba(99,102,241,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <span style={{ fontSize: '0.7rem', color: '#334155' }}>
            <i className="bi bi-arrow-repeat me-1"></i>Auto-refreshes every 15s
          </span>
          <button
            onClick={() => { onClose(); navigate('/analytics/summary'); }}
            style={{ background: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.3)', borderRadius: 8, padding: '6px 18px', color: '#a5b4fc', fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
          >
            <i className="bi bi-bar-chart-line"></i>Full Analytics →
          </button>
        </div>
      </div>
    </div>
  );
};

const smallBtn: React.CSSProperties = {
  background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: 8, padding: '6px 10px', color: '#64748b', cursor: 'pointer', display: 'flex', alignItems: 'center',
};

// ─── Hub Card ─────────────────────────────────────────────────────────────────
const HubCard: React.FC<{ card: AssetCard; onClick: () => void }> = ({ card, onClick }) => {
  const { asset, liveUsers, monStatus, serversTotal, serversDown, securityWarnings, loading } = card;
  const health = cardHealth(card);
  const isDown = monStatus === 'down';

  const bubbleColor = health === 'critical' ? '#ef4444' : health === 'warn' ? '#f59e0b' : '#10b981';
  const bubbleAnim = health === 'critical' ? 'hubRedPulse 1.2s ease-in-out infinite' : health === 'warn' ? 'hubYellowPulse 1.8s ease-in-out infinite' : 'hubGreenPulse 3s ease-in-out infinite';

  const monLabel = monStatus === 'up' ? 'Online' : monStatus === 'down' ? 'Down' : monStatus === 'degraded' ? 'Degraded' : monStatus === 'no_urls' ? 'No URL' : '';
  const monColor = monStatus === 'up' ? '#10b981' : monStatus === 'down' ? '#ef4444' : monStatus === 'degraded' ? '#f59e0b' : '#64748b';
  const monIcon = monStatus === 'up' ? 'bi-check-circle-fill' : monStatus === 'down' ? 'bi-x-circle-fill' : monStatus === 'degraded' ? 'bi-exclamation-triangle-fill' : 'bi-dash-circle';

  return (
    <div
      onClick={onClick}
      title="Click to view details"
      style={{
        background: 'linear-gradient(135deg, rgba(99,102,241,0.08) 0%, rgba(15,15,31,0.95) 100%)',
        borderRadius: 16,
        border: health === 'critical'
          ? '1.5px solid rgba(239,68,68,0.5)'
          : health === 'warn'
          ? '1.5px solid rgba(245,158,11,0.4)'
          : '1.5px solid rgba(99,102,241,0.25)',
        padding: '16px 18px',
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        position: 'relative',
        overflow: 'hidden',
        transition: 'box-shadow .2s, border-color .3s, transform .15s',
        boxShadow: health === 'critical'
          ? '0 0 20px rgba(239,68,68,0.15)'
          : health === 'warn'
          ? '0 0 16px rgba(245,158,11,0.1)'
          : '0 2px 16px rgba(0,0,0,0.3)',
        cursor: 'pointer',
      }}
      onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-2px)'; (e.currentTarget as HTMLDivElement).style.boxShadow = '0 8px 28px rgba(99,102,241,0.2)'; }}
      onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.transform = ''; (e.currentTarget as HTMLDivElement).style.boxShadow = health === 'critical' ? '0 0 20px rgba(239,68,68,0.15)' : '0 2px 16px rgba(0,0,0,0.3)'; }}
    >
      {/* Subtle top accent bar */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, height: 3,
        background: `linear-gradient(90deg, ${bubbleColor}88, ${bubbleColor}22)`,
        borderRadius: '16px 16px 0 0',
      }} />

      {/* Loading shimmer */}
      {loading && (
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(15,15,31,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2, borderRadius: 14 }}>
          <div className="spinner-border spinner-border-sm" style={{ color: '#6366f1' }}></div>
        </div>
      )}

      {/* ── Header row: name + blinking status bubble ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', paddingTop: 4 }}>
        <div style={{ flex: 1, minWidth: 0, paddingRight: 12 }}>
          <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#e2e8f0', lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {asset.name}
          </div>
          <div style={{ fontSize: '0.65rem', color: '#475569', marginTop: 2, fontFamily: 'monospace' }}>
            {asset.tracking_id}
          </div>
        </div>

        {/* Blinking status bubble */}
        <div style={{
          width: 14, height: 14, borderRadius: '50%', flexShrink: 0, marginTop: 3,
          background: bubbleColor,
          animation: bubbleAnim,
          boxShadow: `0 0 0 0 ${bubbleColor}88`,
        }} />
      </div>

      {/* ── Stats grid ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>

        {/* Live users — or DOWN indicator */}
        <div style={{
          background: isDown ? 'rgba(239,68,68,0.1)' : 'rgba(16,185,129,0.08)',
          borderRadius: 10, padding: '9px 11px',
          border: isDown ? '1px solid rgba(239,68,68,0.25)' : '1px solid rgba(16,185,129,0.15)',
        }}>
          {isDown ? (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, lineHeight: 1 }}>
                <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#ef4444', flexShrink: 0, animation: 'hubRedPulse 1.2s ease-in-out infinite' }} />
                <span style={{ fontSize: '1rem', fontWeight: 800, color: '#ef4444' }}>DOWN</span>
              </div>
              <div style={{ fontSize: '0.62rem', color: '#6b7280', marginTop: 3, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Live Stopped
              </div>
            </>
          ) : (
            <>
              <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#34d399', lineHeight: 1, display: 'flex', alignItems: 'center', gap: 4 }}>
                <WaveIcon color="#34d399" />
                {liveUsers}
              </div>
              <div style={{ fontSize: '0.62rem', color: '#6b7280', marginTop: 3, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Live Now
              </div>
            </>
          )}
        </div>

        {/* Servers */}
        <div style={{
          background: serversDown > 0 ? 'rgba(239,68,68,0.08)' : 'rgba(99,102,241,0.07)',
          borderRadius: 10, padding: '9px 11px',
          border: `1px solid ${serversDown > 0 ? 'rgba(239,68,68,0.2)' : 'rgba(99,102,241,0.15)'}`,
        }}>
          <div style={{ fontSize: '1.4rem', fontWeight: 800, color: serversDown > 0 ? '#f87171' : '#a5b4fc', lineHeight: 1, display: 'flex', alignItems: 'center' }}>
            {serversTotal > 0 ? (
              <>
                <HeartbeatIcon color={serversDown > 0 ? '#f87171' : '#a5b4fc'} />
                <span>{serversTotal - serversDown}</span>
                <span style={{ fontSize: '0.75rem', color: '#475569', fontWeight: 500, marginLeft: 2 }}>/{serversTotal}</span>
              </>
            ) : (
              <span style={{ fontSize: '0.9rem', color: '#334155' }}>—</span>
            )}
          </div>
          <div style={{ fontSize: '0.62rem', color: '#6b7280', marginTop: 3, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {serversDown > 0 ? `${serversDown} DOWN` : 'Servers'}
          </div>
        </div>

        {/* Monitor status */}
        <div style={{
          background: monStatus === 'down' ? 'rgba(239,68,68,0.08)' : monStatus === 'degraded' ? 'rgba(245,158,11,0.08)' : monStatus === 'up' ? 'rgba(16,185,129,0.07)' : 'rgba(255,255,255,0.03)',
          borderRadius: 10, padding: '9px 11px',
          border: `1px solid ${monStatus === 'down' ? 'rgba(239,68,68,0.2)' : monStatus === 'up' ? 'rgba(16,185,129,0.15)' : 'rgba(255,255,255,0.06)'}`,
        }}>
          <div style={{ fontSize: '1.1rem', fontWeight: 700, color: monColor, lineHeight: 1 }}>
            <i className={`bi ${monIcon}`}></i>
          </div>
          <div style={{ fontSize: '0.62rem', color: '#6b7280', marginTop: 3, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {monLabel || 'Monitor'}
          </div>
        </div>

        {/* Security */}
        <div style={{
          background: securityWarnings > 0 ? 'rgba(245,158,11,0.08)' : 'rgba(255,255,255,0.03)',
          borderRadius: 10, padding: '9px 11px',
          border: `1px solid ${securityWarnings > 0 ? 'rgba(245,158,11,0.2)' : 'rgba(255,255,255,0.06)'}`,
        }}>
          <div style={{ fontSize: '1.1rem', fontWeight: 700, color: securityWarnings > 0 ? '#fbbf24' : '#10b981', lineHeight: 1 }}>
            {securityWarnings > 0
              ? <><i className="bi bi-shield-exclamation"></i> <span style={{ fontSize: '1rem' }}>{securityWarnings}</span></>
              : <i className="bi bi-shield-check"></i>}
          </div>
          <div style={{ fontSize: '0.62rem', color: '#6b7280', marginTop: 3, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Security
          </div>
        </div>
      </div>

      {/* Click hint */}
      <div style={{ position: 'absolute', bottom: 8, right: 10, fontSize: '9px', color: '#334155', opacity: 0.7 }}>
        click for details
      </div>
    </div>
  );
};

// ─── Main Page ─────────────────────────────────────────────────────────────────
const LiveHubPage: React.FC = () => {
  useDocumentTitle('Live Monitoring Hub');
  const navigate = useNavigate();
  const { user } = useAuth();

  const [allAssets, setAllAssets] = useState<HubAsset[]>([]);
  const [cards, setCards] = useState<AssetCard[]>([]);
  const [cardsPerView, setCardsPerView] = useState<number>(() => parseInt(localStorage.getItem('hub_cards_per_view') || '6'));
  const [refreshInterval, setRefreshInterval] = useState<number>(() => parseInt(localStorage.getItem('hub_refresh_interval') || '30'));
  const [showSettings, setShowSettings] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState<Date>(new Date());
  const [page, setPage] = useState(0);
  const [selectedCard, setSelectedCard] = useState<AssetCard | null>(null);
  const refreshRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Load all assets
  useEffect(() => {
    assetsAPI.list().then(res => {
      const raw = res.data?.data?.assets || res.data?.data || [];
      const assets: HubAsset[] = raw.map((a: any) => ({ id: a.id, name: a.name, tracking_id: a.tracking_id }));
      setAllAssets(assets);
      setCards(assets.map(asset => ({
        asset, liveUsers: 0, monStatus: 'loading', serversTotal: 0, serversDown: 0, securityWarnings: 0, loading: true,
      })));
    }).catch(() => {});
  }, []);

  // Fetch all card data per asset
  const fetchCardData = useCallback(async (assets: HubAsset[]) => {
    const updated = await Promise.all(assets.map(async asset => {
      let liveUsers = 0;
      let monStatus: MonStatus = 'no_urls';
      let serversTotal = 0;
      let serversDown = 0;
      let securityWarnings = 0;

      await Promise.allSettled([
        analyticsAPI.getLiveVisitors(asset.id).then(r => {
          liveUsers = r.data?.data?.onlineCount ?? r.data?.data?.live_count ?? 0;
        }),
        monitoringAPI.getHealthStatus(asset.id).then(r => {
          const d = r.data?.data;
          if (d?.overall_status) monStatus = d.overall_status as MonStatus;
        }),
        serverAPI.list({ asset_id: asset.id }).then(r => {
          const servers: any[] = r.data?.data?.servers || r.data?.data || [];
          serversTotal = servers.length;
          serversDown = servers.filter((s: any) => s.status === 'offline' || s.status === 'down').length;
        }),
      ]);

      return { asset, liveUsers, monStatus, serversTotal, serversDown, securityWarnings, loading: false };
    }));

    setCards(updated);
    setLastRefreshed(new Date());

    // Keep selected card data fresh
    setSelectedCard(prev => {
      if (!prev) return null;
      const fresh = updated.find(c => c.asset.id === prev.asset.id);
      return fresh ?? prev;
    });
  }, []);

  useEffect(() => {
    if (allAssets.length > 0) fetchCardData(allAssets);
  }, [allAssets, fetchCardData]);

  useEffect(() => {
    if (refreshRef.current) clearInterval(refreshRef.current);
    refreshRef.current = setInterval(() => {
      if (allAssets.length > 0) fetchCardData(allAssets);
    }, refreshInterval * 1000);
    return () => { if (refreshRef.current) clearInterval(refreshRef.current); };
  }, [allAssets, fetchCardData, refreshInterval]);

  const totalPages = Math.ceil(cards.length / cardsPerView);
  const visibleCards = cards.slice(page * cardsPerView, (page + 1) * cardsPerView);
  const cols = cardsPerView <= 4 ? 2 : cardsPerView <= 6 ? 3 : 4;

  const criticalCount = cards.filter(c => cardHealth(c) === 'critical').length;
  const warnCount = cards.filter(c => cardHealth(c) === 'warn').length;

  const saveSettings = (c: number, i: number) => {
    localStorage.setItem('hub_cards_per_view', String(c));
    localStorage.setItem('hub_refresh_interval', String(i));
    setCardsPerView(c); setRefreshInterval(i); setShowSettings(false);
  };

  return (
    <div style={{ minHeight: '100vh', background: '#0d0d1f', display: 'flex', flexDirection: 'column', fontFamily: '-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif' }}>

      {/* ── Header ── */}
      <div style={{
        background: 'rgba(99,102,241,0.06)',
        borderBottom: '1px solid rgba(99,102,241,0.2)',
        padding: '12px 24px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          {/* Logo */}
          <div style={{ fontWeight: 800, fontSize: '1rem', color: '#e2e8f0', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', borderRadius: 9, width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <i className="bi bi-broadcast" style={{ color: '#fff', fontSize: 15 }}></i>
            </span>
            Live Monitoring Hub
          </div>

          {/* Status pill */}
          {criticalCount > 0 ? (
            <div style={{ background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 20, padding: '3px 12px', display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#ef4444', animation: 'hubRedPulse 1s infinite', display: 'inline-block' }}></span>
              <span style={{ fontSize: '0.72rem', color: '#f87171', fontWeight: 700 }}>{criticalCount} CRITICAL</span>
            </div>
          ) : warnCount > 0 ? (
            <div style={{ background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 20, padding: '3px 12px', display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#f59e0b', animation: 'hubYellowPulse 1.5s infinite', display: 'inline-block' }}></span>
              <span style={{ fontSize: '0.72rem', color: '#fbbf24', fontWeight: 700 }}>{warnCount} WARNING</span>
            </div>
          ) : cards.length > 0 && !cards.some(c => c.loading) ? (
            <div style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.25)', borderRadius: 20, padding: '3px 12px', display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#10b981', animation: 'hubGreenPulse 2.5s infinite', display: 'inline-block' }}></span>
              <span style={{ fontSize: '0.72rem', color: '#34d399', fontWeight: 700 }}>ALL SYSTEMS OPERATIONAL</span>
            </div>
          ) : null}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ color: '#475569', fontSize: '0.72rem' }}>
            <i className="bi bi-clock me-1"></i>{lastRefreshed.toLocaleTimeString()}
          </span>
          <button onClick={() => allAssets.length > 0 && fetchCardData(allAssets)} style={btnStyle('#334155', '#94a3b8')}>
            <i className="bi bi-arrow-clockwise"></i>
          </button>
          <button onClick={() => setShowSettings(true)} style={btnStyle('#334155', '#94a3b8')}>
            <i className="bi bi-sliders me-1"></i>Settings
          </button>
          <button onClick={() => navigate('/analytics/summary')} style={btnStyle('rgba(239,68,68,0.15)', '#f87171', 'rgba(239,68,68,0.3)')}>
            <i className="bi bi-x-lg me-1"></i>Exit
          </button>
        </div>
      </div>

      {/* ── Cards Grid ── */}
      <div style={{ flex: 1, padding: '20px 24px', overflowY: 'auto' }}>
        {cards.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#334155', paddingTop: '5rem' }}>
            <i className="bi bi-grid-3x3-gap" style={{ fontSize: '3rem', display: 'block', marginBottom: 16, color: '#4c1d95' }}></i>
            <p style={{ color: '#475569' }}>No assets found. Add an asset to get started.</p>
            <button className="btn btn-primary" onClick={() => navigate('/setup/asset')}>Add Asset</button>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 16 }}>
            {visibleCards.map(card => (
              <HubCard
                key={card.asset.id}
                card={card}
                onClick={() => setSelectedCard(card)}
              />
            ))}
          </div>
        )}
      </div>

      {/* ── Pagination ── */}
      {totalPages > 1 && (
        <div style={{ padding: '10px 24px', background: 'rgba(99,102,241,0.05)', borderTop: '1px solid rgba(99,102,241,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, flexShrink: 0 }}>
          <button disabled={page === 0} onClick={() => setPage(p => p - 1)} style={btnStyle('#1e1b4b', '#6366f1')}>
            <i className="bi bi-chevron-left"></i>
          </button>
          {Array.from({ length: totalPages }, (_, i) => (
            <button key={i} onClick={() => setPage(i)} style={{ ...btnStyle(i === page ? '#6366f1' : '#1e1b4b', '#fff'), minWidth: 34 }}>
              {i + 1}
            </button>
          ))}
          <button disabled={page === totalPages - 1} onClick={() => setPage(p => p + 1)} style={btnStyle('#1e1b4b', '#6366f1')}>
            <i className="bi bi-chevron-right"></i>
          </button>
          <span style={{ color: '#475569', fontSize: '0.72rem', marginLeft: 8 }}>{cards.length} assets</span>
        </div>
      )}

      {/* ── Card Detail Modal ── */}
      {selectedCard && (
        <CardDetailModal
          card={selectedCard}
          onClose={() => setSelectedCard(null)}
        />
      )}

      {showSettings && (
        <SettingsModal current={{ cardsPerView, refreshInterval }} onSave={saveSettings} onClose={() => setShowSettings(false)} />
      )}

      <style>{`
        @keyframes hubWave {
          0%, 100% { transform: scaleY(0.4); }
          50% { transform: scaleY(1); }
        }
        @keyframes hubBeat {
          0%, 100% { transform: scale(1); }
          14%       { transform: scale(1.35); }
          28%       { transform: scale(1); }
          42%       { transform: scale(1.2); }
          70%       { transform: scale(1); }
        }
        @keyframes hubRedPulse {
          0%   { box-shadow: 0 0 0 0 rgba(239,68,68,0.7); }
          70%  { box-shadow: 0 0 0 8px rgba(239,68,68,0); }
          100% { box-shadow: 0 0 0 0 rgba(239,68,68,0); }
        }
        @keyframes hubYellowPulse {
          0%   { box-shadow: 0 0 0 0 rgba(245,158,11,0.7); }
          70%  { box-shadow: 0 0 0 7px rgba(245,158,11,0); }
          100% { box-shadow: 0 0 0 0 rgba(245,158,11,0); }
        }
        @keyframes hubGreenPulse {
          0%   { box-shadow: 0 0 0 0 rgba(16,185,129,0.6); }
          70%  { box-shadow: 0 0 0 6px rgba(16,185,129,0); }
          100% { box-shadow: 0 0 0 0 rgba(16,185,129,0); }
        }
      `}</style>
    </div>
  );
};

// ─── Button style helper ───────────────────────────────────────────────────────
const btnStyle = (bg: string, color: string, border?: string): React.CSSProperties => ({
  background: bg, color, border: `1px solid ${border || 'rgba(255,255,255,0.08)'}`,
  borderRadius: 8, padding: '5px 12px', cursor: 'pointer', fontSize: '0.8rem',
  fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 4,
  transition: 'opacity .15s',
});

// ─── Settings Modal ────────────────────────────────────────────────────────────
const SettingsModal: React.FC<{
  current: { cardsPerView: number; refreshInterval: number };
  onSave: (cards: number, interval: number) => void;
  onClose: () => void;
}> = ({ current, onSave, onClose }) => {
  const [cards, setCards] = useState(current.cardsPerView);
  const [interval, setInterval] = useState(current.refreshInterval);

  const optBtn = (active: boolean, color: string): React.CSSProperties => ({
    flex: 1, padding: '8px 0', borderRadius: 8, border: '1px solid',
    borderColor: active ? color : 'rgba(255,255,255,0.1)',
    background: active ? `${color}22` : 'rgba(255,255,255,0.03)',
    color: active ? color : '#64748b', cursor: 'pointer', fontWeight: 700, fontSize: '0.85rem',
    transition: 'all .15s',
  });

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#13132a', borderRadius: 16, padding: 28, width: 380, boxShadow: '0 24px 60px rgba(0,0,0,0.6)', border: '1px solid rgba(99,102,241,0.3)' }}>
        <h5 style={{ color: '#e2e8f0', marginBottom: 24, display: 'flex', alignItems: 'center', gap: 8 }}>
          <i className="bi bi-sliders" style={{ color: '#6366f1' }}></i>Hub Settings
        </h5>

        <div style={{ marginBottom: 20 }}>
          <label style={{ color: '#64748b', fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 10 }}>
            Cards per view
          </label>
          <div style={{ display: 'flex', gap: 8 }}>
            {CARDS_OPTIONS.map(n => (
              <button key={n} onClick={() => setCards(n)} style={optBtn(cards === n, '#6366f1')}>{n}</button>
            ))}
          </div>
        </div>

        <div style={{ marginBottom: 24 }}>
          <label style={{ color: '#64748b', fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 10 }}>
            Auto-refresh
          </label>
          <div style={{ display: 'flex', gap: 8 }}>
            {REFRESH_OPTIONS.map(opt => (
              <button key={opt.value} onClick={() => setInterval(opt.value)} style={optBtn(interval === opt.value, '#8b5cf6')}>{opt.label}</button>
            ))}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={onClose} style={{ flex: 1, padding: '10px 0', borderRadius: 8, border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.04)', color: '#64748b', cursor: 'pointer', fontWeight: 600 }}>
            Cancel
          </button>
          <button onClick={() => onSave(cards, interval)} style={{ flex: 1, padding: '10px 0', borderRadius: 8, border: 'none', background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', color: '#fff', cursor: 'pointer', fontWeight: 700 }}>
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default LiveHubPage;
